import { useEffect, useRef, useState } from 'react';
import { Routes, Route, NavLink, useNavigate } from 'react-router-dom';
import { collection, query, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { MarketProvider, inPortals } from '../../contexts/MarketContext';
import PortalSwitcher from '../../components/PortalSwitcher';
import { LocaleProvider } from '../../contexts/LocaleContext';
import DashboardHome from '../staff/DashboardHome';
import GoalsPage from '../staff/GoalsPage';
import GoalsSplash from '../../components/GoalsSplash';
import TourCalendar from '../staff/TourCalendar';
import Requests from '../staff/Requests';
import RequestDetail from '../staff/RequestDetail';
import StaffNewRequest from '../staff/NewRequest';
import Invoices from '../staff/Invoices';
import TeamDatabasePage from '../staff/TeamDatabasePage';
import UserSettings from '../staff/UserSettings';

// Spanish-side portal (Spain → UK tours). It reuses the exact same tour engine
// as the UK staff portal, mounted under /es and scoped to the spain_to_uk
// market. The nav is deliberately slimmed to the four things Spanish staff need:
// Dashboard, Calendar, Tours and Invoices.

const NAV_ICONS = {
  '/es':          <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>,
  '/es/goals':    <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1.5" /></svg>,
  '/es/calendar': <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>,
  '/es/requests': <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064" /></svg>,
  '/es/invoices': <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z" /></svg>,
  '/es/team-database': <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M4 7v10c0 2 4 3 8 3s8-1 8-3V7M4 7c0 2 4 3 8 3s8-1 8-3M4 7c0-2 4-3 8-3s8 1 8 3m0 5c0 2-4 3-8 3s-8-1-8-3" /></svg>,
};

function SidebarLink({ to, label, badge, end, collapsed }) {
  const icon = NAV_ICONS[to] || null;
  return (
    <NavLink
      to={to}
      end={end}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        `relative flex items-center gap-[11px] rounded-[9px] text-[13.5px] font-semibold transition-colors ${
          collapsed ? 'px-[10px] py-[9px] justify-center' : 'px-[11px] py-[9px]'
        } ${
          isActive
            ? 'bg-blue-light text-pb'
            : 'text-[#6B7280] hover:bg-surface hover:text-[#111827]'
        }`
      }
    >
      {({ isActive }) => (
        <>
          {isActive && !collapsed && (
            <span className="absolute left-0 top-[8px] bottom-[8px] w-[3px] bg-pb rounded-r-full" />
          )}
          {icon && (
            <span className={`w-[18px] h-[18px] flex-shrink-0 ${isActive ? 'text-pb' : ''}`}>
              {icon}
            </span>
          )}
          {!collapsed && <span className="flex-1 truncate">{label}</span>}
          {!collapsed && badge != null && badge > 0 && (
            <span className={`text-[10.5px] font-extrabold px-[7px] py-0.5 rounded-[20px] text-white ml-auto flex-shrink-0 ${
              isActive ? 'bg-pb' : 'bg-ba'
            }`}>
              {badge}
            </span>
          )}
          {collapsed && badge != null && badge > 0 && (
            <span className="absolute top-1 right-1 w-[6px] h-[6px] rounded-full bg-ba" />
          )}
        </>
      )}
    </NavLink>
  );
}

const SIDEBAR_COLLAPSED_KEY = 'mst_sidebar_collapsed';

export default function SpainDashboard() {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [submittedCount, setSubmittedCount] = useState(0);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true'; } catch { return false; }
  });
  // The persisted collapse preference only applies to the static desktop rail.
  // On mobile the sidebar is a full-width overlay drawer (sidebarOpen), and its
  // collapse toggle isn't shown there, so a collapsed value carried over from
  // desktop must not force an icon-only, unescapable drawer.
  const effectiveCollapsed = sidebarOpen ? false : sidebarCollapsed;
  const userMenuRef = useRef(null);

  function toggleCollapsed() {
    setSidebarCollapsed((c) => {
      try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(!c)); } catch {}
      return !c;
    });
  }

  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, 'requests')), (snap) => {
      // New Spain → UK tours awaiting first review.
      setSubmittedCount(snap.docs.filter((d) => {
        const data = d.data();
        return inPortals(data, ['spain_to_uk'])
          && data.category === 'sales_admin' && !data.stageProgress?.initial_interest;
      }).length);
    });
    return unsub;
  }, []);

  useEffect(() => {
    function handler(e) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  async function handleLogout() {
    await logout();
    navigate('/login');
  }

  const initials = currentUser?.email?.[0]?.toUpperCase() || '?';
  const displayName = currentUser?.displayName || currentUser?.email || '';

  const MAIN_NAV = [
    { label: 'Panel',      to: '/es',           end: true },
    { label: 'Objetivos',  to: '/es/goals' },
    { label: 'Calendario', to: '/es/calendar' },
    { label: 'Tours',      to: '/es/requests',  badge: submittedCount },
    { label: 'Facturas',   to: '/es/invoices' },
    { label: 'Bases de datos', to: '/es/team-database' },
  ];

  return (
    <div className="min-h-screen bg-page flex flex-col">

      {/* ── Topbar ── */}
      <header className="h-[58px] bg-navy flex items-center justify-between px-4 flex-shrink-0 z-20">
        <div className="flex items-center gap-[14px]">
          <button
            onClick={() => {
              if (window.matchMedia('(min-width: 768px)').matches) toggleCollapsed();
              else setSidebarOpen(o => !o);
            }}
            className="w-9 h-9 flex items-center justify-center rounded-[9px] bg-white/10 hover:bg-white/20 transition flex-shrink-0"
            aria-label="Toggle navigation"
          >
            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <img src="/MST Logo White.png" alt="MST Sports Tours" style={{ height: 26, display: 'block', flexShrink: 0 }} />
          <span style={{ width: 1, alignSelf: 'stretch', background: 'rgba(255,255,255,.16)', margin: '10px 0', flexShrink: 0 }} />
          <span className="hidden sm:inline text-white/[0.62] text-[13.5px] font-semibold tracking-[0.2px] whitespace-nowrap">
            Portal España · Spain → UK
          </span>
          <PortalSwitcher />
        </div>

        <div className="flex items-center gap-[10px]">
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setUserMenuOpen((o) => !o)}
              aria-label="User menu"
              aria-expanded={userMenuOpen}
              className="flex items-center gap-2 bg-white/10 hover:bg-white/20 rounded-[22px] px-3 py-[5px] transition"
            >
              <span className="w-[30px] h-[30px] rounded-full flex items-center justify-center text-[12px] font-bold text-white flex-shrink-0"
                style={{ background: 'linear-gradient(135deg,#2C5FA0,#1A4A8A)' }}>
                {initials}
              </span>
              <div className="hidden sm:block leading-[1.15]">
                <div className="text-[12.5px] font-bold text-white whitespace-nowrap max-w-[120px] truncate">{displayName}</div>
                <div className="text-[10.5px] text-white/60">España</div>
              </div>
              <svg className={`w-[13px] h-[13px] text-white/60 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
              </svg>
            </button>

            {userMenuOpen && (
              <div className="absolute top-full right-0 mt-2 w-44 bg-white border border-[#E5E7EB] rounded-[12px] shadow-lg overflow-hidden z-50">
                <NavLink
                  to="/es/user-settings"
                  onClick={() => setUserMenuOpen(false)}
                  className={({ isActive }) =>
                    `block px-4 py-2.5 text-[13px] font-medium transition ${
                      isActive ? 'bg-blue-light text-pb' : 'text-[#374151] hover:bg-surface'
                    }`
                  }
                >
                  Ajustes
                </NavLink>
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-2.5 text-[13px] font-medium text-dr hover:bg-red-light transition"
                >
                  Cerrar sesión
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ── Below topbar ── */}
      <div className="flex flex-1 relative">

        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/40 z-30 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* ── Sidebar ── */}
        <aside className={`
          flex flex-shrink-0 flex-col py-3 overflow-y-auto
          bg-white border-r border-[#E5E7EB]
          fixed top-[58px] bottom-0 left-0 z-40
          transition-all duration-200 ease-in-out
          md:static md:top-auto md:bottom-auto md:z-auto md:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
          w-[232px] ${sidebarCollapsed ? 'md:w-[60px]' : 'md:w-[232px]'}
        `}>
          <nav className="px-2 flex flex-col gap-[2px] flex-1" onClick={() => setSidebarOpen(false)}>
            {MAIN_NAV.map(({ label, to, badge, end }) => (
              <SidebarLink key={to} to={to} label={label} badge={badge} end={end} collapsed={effectiveCollapsed} />
            ))}
          </nav>

          {!effectiveCollapsed && (
            <div className="border-t border-[#E5E7EB] mx-[10px] mt-3 pt-3 px-1 flex items-center gap-[9px]">
              <span className="w-[9px] h-[9px] rounded-full bg-sg flex-shrink-0"
                style={{ boxShadow: '0 0 0 3px rgba(26,122,74,.18)' }} />
              <div>
                <div className="text-[11.5px] font-bold text-[#374151] whitespace-nowrap">Todo operativo</div>
                <div className="text-[10.5px] text-[#9CA3AF]">Sincronizado</div>
              </div>
            </div>
          )}

          <div className="hidden md:flex border-t border-[#E5E7EB] mt-2 pt-2 px-2">
            <button
              onClick={toggleCollapsed}
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className="flex items-center justify-center w-full py-2 rounded-[8px] text-[#9CA3AF] hover:text-[#6B7280] hover:bg-surface transition"
            >
              <svg
                className={`w-4 h-4 transition-transform duration-200 ${sidebarCollapsed ? 'rotate-180' : ''}`}
                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
              {!sidebarCollapsed && <span className="ml-2 text-[12px]">Contraer</span>}
            </button>
          </div>
        </aside>

        {/* ── Main content ── */}
        <main className="flex-1 px-4 py-4 md:px-[26px] md:py-[24px] overflow-y-auto min-w-0 page-fade">
          <LocaleProvider locale="es">
          <MarketProvider portals={['spain_to_uk']}>
            <GoalsSplash locale="es" />
            <Routes>
              <Route index element={<DashboardHome />} />
              <Route path="goals" element={<GoalsPage locale="es" />} />
              <Route path="calendar" element={<TourCalendar />} />
              <Route path="requests" element={<Requests />} />
              <Route path="requests/new" element={<StaffNewRequest market="spain_to_uk" basePath="/es" />} />
              <Route path="requests/:id" element={<RequestDetail />} />
              <Route path="invoices" element={<Invoices />} />
              <Route path="team-database" element={<TeamDatabasePage />} />
              <Route path="user-settings" element={<UserSettings />} />
            </Routes>
          </MarketProvider>
          </LocaleProvider>
        </main>
      </div>
    </div>
  );
}
